import { errorReporter } from '@flow/logging';

import { isDiscountNone, isDiscountNumeric, isTaxNone } from '@flow/libs/agreement/boxes/product';
import type {
  Column,
  DiscountType,
  PriceRoundingMethod,
  ProductTableBox,
  ProductValue,
  TaxType,
} from '@flow/libs/data-validators/entity-schemas/document-box/product-table';
import { roundBy } from '@flow/libs/utils/math';

import type { PriceKeysType, PriceType } from '@flow/libs/agreement/boxes/product-constants';
import {
  COUNT_KEY,
  DEFAULT_PERCENTAGE_ROUNDING_PRECISION,
  DEFAULT_QUANTITY_PRECISION,
  PRICE_KEYS,
  PRICE_ROUNDING_METHOD,
} from '@flow/libs/agreement/boxes/product-constants';

// --- Input & Type Assertions ---

function assertProductTableBoxObject(productTableBox: ProductTableBox) {
  if (!productTableBox?.config?.columns || !productTableBox?.content?.data) {
    throw new Error(
      `Invalid product table box structure. Expected columns and data but received '${JSON.stringify(productTableBox)}'.`,
    );
  }
}

function assertProductValueObject(productValue: ProductValue) {
  if (!productValue || typeof productValue !== 'object') {
    throw new Error(
      `Invalid product value. Expected an object but received '${productValue}' of type '${typeof productValue}'.`,
    );
  }
}

// --- Column & Numeric Utilities ---

function getColumnByKey<K extends Column['key']>(
  columns: Column[],
  key: K,
): Extract<
  Column,
  {
    key: K;
  }
> {
  const column = columns.find(
    (
      col,
    ): col is Extract<
      Column,
      {
        key: K;
      }
    > => col.key === key,
  );

  if (!column) {
    throw new Error(`getColumnByKey: Column with key '${key}' not found.`);
  }

  return column;
}

function getNumericalValue(value: string | number): number {
  const numericalValue = Number(value);

  if (Number.isNaN(numericalValue)) {
    throw new Error(
      `getNumericalValue: Invalid numerical value. Expected a string or number and received '${value}' of type '${typeof value}'.`,
    );
  }

  return numericalValue;
}

// --- Extraction Helpers ---

function extractNumericalField(
  productValue: ProductValue,
  key: keyof PriceKeysType,
  fallback = 0,
): number {
  return getNumericalValue(productValue[key] ?? fallback);
}

function extractField<T extends keyof PriceKeysType>(
  productValue: ProductValue,
  key: T,
): ProductValue[T] {
  return productValue[key];
}

const makeDiscountTypeKey = (priceType: PriceType) => `${priceType}_discount_type` as const;
const makeDiscountAmountKey = (priceType: PriceType) => `${priceType}_discount_amount` as const;
const makeTaxTypeKey = (priceType: PriceType) => `${priceType}_tax_1_type` as const;
const makeTaxAmountKey = (priceType: PriceType) => `${priceType}_tax_1_amount` as const;

// --- Field Extractors ---

export function getRawPrice(productValue: ProductValue, priceTypeKey: PriceType): number {
  return extractNumericalField(productValue, priceTypeKey, 0);
}

export function getDiscountType(productValue: ProductValue, priceTypeKey: PriceType): DiscountType {
  return extractField(productValue, makeDiscountTypeKey(priceTypeKey));
}

export function getRawDiscountAmount(productValue: ProductValue, priceTypeKey: PriceType): number {
  return extractNumericalField(productValue, makeDiscountAmountKey(priceTypeKey), 0);
}

export function getRawQuantity(
  productValue: ProductValue,
  priceTypeKey: PriceType,
  columns: Column[],
  treatHiddenCountAsOne?: boolean,
): number {
  const rawQuantity = productValue.count ?? 0;

  const countColumn = getColumnByKey(columns, COUNT_KEY);
  const isCountColumnDisabled = !countColumn.enabled;

  if (treatHiddenCountAsOne && isCountColumnDisabled) {
    return 1;
  }

  const currentPriceColumn = getColumnByKey(columns, priceTypeKey);
  const isPriceColumnFixedAmount = currentPriceColumn.fixedAmount;

  if (isPriceColumnFixedAmount) {
    return Math.min(rawQuantity, 1);
  }

  return rawQuantity;
}

export function getTaxType(productValue: ProductValue, priceTypeKey: PriceType): TaxType {
  return extractField(productValue, makeTaxTypeKey(priceTypeKey));
}

export function getRawTaxAmount(productValue: ProductValue, priceTypeKey: PriceType): number {
  return extractNumericalField(productValue, makeTaxAmountKey(priceTypeKey), 0);
}

// --- Base Price & Discount Calculators ---

export function getBasePrice(
  rawPrice: number,
  priceRoundingMethod: PriceRoundingMethod,
  pricePrecision: number,
): number {
  return roundBy(priceRoundingMethod, rawPrice, pricePrecision);
}

export function getDiscountAmount(
  basePrice: number,
  discountType: DiscountType,
  rawDiscountAmount: number,
  priceRoundingMethod: PriceRoundingMethod,
  pricePrecision: number,
): number {
  if (isDiscountNone(discountType)) {
    return 0;
  }

  if (isDiscountNumeric(discountType)) {
    return roundBy(priceRoundingMethod, rawDiscountAmount, pricePrecision);
  }

  const discountRate = roundBy(
    priceRoundingMethod,
    rawDiscountAmount,
    DEFAULT_PERCENTAGE_ROUNDING_PRECISION,
  );

  return roundBy(priceRoundingMethod, basePrice * discountRate, pricePrecision);
}

export function getPriceDiscounted(
  basePrice: number,
  discountAmount: number,
  priceRoundingMethod: PriceRoundingMethod,
  pricePrecision: number,
) {
  return roundBy(priceRoundingMethod, basePrice - discountAmount, pricePrecision);
}

export function getQuantity(
  rawQuantity: number,
  priceRoundingMethod: PriceRoundingMethod,
  quantityPrecision: number,
): number {
  return roundBy(priceRoundingMethod, rawQuantity, quantityPrecision);
}

// --- Row Total & Tax Calculators ---

export function getRowTotal(
  price: number,
  quantity: number,
  priceRoundingMethod: PriceRoundingMethod,
  pricePrecision: number,
) {
  return roundBy(priceRoundingMethod, price * quantity, pricePrecision);
}

export function getTaxAmount(
  rowTotalDiscounted: number,
  taxType: TaxType,
  rawTaxAmount: number,
  priceRoundingMethod: PriceRoundingMethod,
  pricePrecision: number,
): number {
  if (isTaxNone(taxType)) {
    return 0;
  }

  const taxRate = roundBy(priceRoundingMethod, rawTaxAmount, DEFAULT_PERCENTAGE_ROUNDING_PRECISION);

  return roundBy(priceRoundingMethod, rowTotalDiscounted * taxRate, pricePrecision);
}

export function getRowTotalInclTax(
  rowTotalDiscounted: number,
  taxAmount: number,
  priceRoundingMethod: PriceRoundingMethod,
  pricePrecision: number,
) {
  return roundBy(priceRoundingMethod, rowTotalDiscounted + taxAmount, pricePrecision);
}

// --- Aggregation & Totals Calculators ---

export function getPriceTypeSums(
  productBox: ProductTableBox,
  {
    priceTypeKey,
    priceRoundingMethod,
    pricePrecision,
    quantityPrecision,
  }: {
    priceTypeKey: PriceType;
    priceRoundingMethod: PriceRoundingMethod;
    pricePrecision: number;
    quantityPrecision: number;
  },
) {
  return productBox.content.data.reduce(
    (sums, product) => {
      assertProductValueObject(product.value);

      if (product._removed) {
        return sums;
      }

      const rawPrice = getRawPrice(product.value, priceTypeKey);
      const rawDiscountAmount = getRawDiscountAmount(product.value, priceTypeKey);
      const rawQuantity = getRawQuantity(
        product.value,
        priceTypeKey,
        productBox.config.columns,
        productBox.config.treatHiddenCountAsOne,
      );
      const rawTaxAmount = getRawTaxAmount(product.value, priceTypeKey);

      const basePrice = getBasePrice(rawPrice, priceRoundingMethod, pricePrecision);
      const discountAmount = getDiscountAmount(
        basePrice,
        getDiscountType(product.value, priceTypeKey),
        rawDiscountAmount,
        priceRoundingMethod,
        pricePrecision,
      );
      const priceDiscounted = getPriceDiscounted(
        basePrice,
        discountAmount,
        priceRoundingMethod,
        pricePrecision,
      );
      const quantity = getQuantity(rawQuantity, priceRoundingMethod, quantityPrecision);

      const rowTotal = getRowTotal(basePrice, quantity, priceRoundingMethod, pricePrecision);
      const rowTotalDiscounted = getRowTotal(
        priceDiscounted,
        quantity,
        priceRoundingMethod,
        pricePrecision,
      );
      const taxAmount = getTaxAmount(
        rowTotalDiscounted,
        getTaxType(product.value, priceTypeKey),
        rawTaxAmount,
        priceRoundingMethod,
        pricePrecision,
      );
      const rowTotalInclTax = getRowTotalInclTax(
        rowTotalDiscounted,
        taxAmount,
        priceRoundingMethod,
        pricePrecision,
      );

      sums.price += rowTotal;
      sums.priceDiscounted += rowTotalDiscounted;
      sums.priceInclTax += rowTotalInclTax;
      sums.taxAmount += taxAmount;

      return sums;
    },
    {
      price: 0,
      priceDiscounted: 0,
      priceInclTax: 0,
      taxAmount: 0,
    },
  );
}

export function getTotalSums(
  productBox: ProductTableBox,
  {
    priceRoundingMethod = PRICE_ROUNDING_METHOD.BANKERS,
    pricePrecision,
    quantityPrecision = DEFAULT_QUANTITY_PRECISION,
  }: {
    priceRoundingMethod: PriceRoundingMethod;
    pricePrecision: number;
    quantityPrecision: number;
  },
) {
  const initialSums = {
    price_1: 0,
    price_1_discounted: 0,
    price_1_incl_tax: 0,
    price_1_tax_1_amount: 0,
    price_2: 0,
    price_2_discounted: 0,
    price_2_incl_tax: 0,
    price_2_tax_1_amount: 0,
  };

  try {
    assertProductTableBoxObject(productBox);

    return PRICE_KEYS.reduce(
      (sums, priceTypeKey) => {
        const totals = getPriceTypeSums(productBox, {
          priceTypeKey,
          priceRoundingMethod,
          pricePrecision,
          quantityPrecision,
        });

        sums[priceTypeKey] += totals.price;
        sums[`${priceTypeKey}_discounted` as keyof typeof sums] += totals.priceDiscounted;
        sums[`${priceTypeKey}_incl_tax` as keyof typeof sums] += totals.priceInclTax;
        sums[`${priceTypeKey}_tax_1_amount` as keyof typeof sums] += totals.taxAmount;

        return sums;
      },
      {
        ...initialSums,
      },
    );
  } catch (error) {
    errorReporter.error(`Error calculating totals: ${(error as Error).message}`);

    return initialSums;
  }
}
